import { useState, useEffect, useContext, createContext } from 'react';
import { getAuth, onAuthStateChanged, signOut, updateEmail, sendEmailVerification, applyActionCode, verifyPasswordResetCode } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc, query, collection, where, getDocs } from 'firebase/firestore';
import app from '../backend/firebaseConfig';
import SplashScreen from '../components/UI/atoms/splash/SplashScreen';
import { hasRouteAccess } from '../config/routeConfig';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const auth = getAuth(app);
  const db = getFirestore(app);

  useEffect(() => {
    // Set up cross-tab communication for email verification
    const broadcastChannel = typeof BroadcastChannel !== 'undefined' 
      ? new BroadcastChannel('email_verification') 
      : null;
    
    // Check for pending email changes that might have been verified (even if user not signed in)
    const checkAndSyncPendingEmailChanges = async () => {
      try {
        // Check URL for Firebase Auth action parameters
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        const oobCode = urlParams.get('oobCode');
        
        // If we're coming back from a verification link, handle it
        if ((mode === 'verifyAndChangeEmail' || mode === 'verifyEmail') && oobCode) {
          try {
            // Apply the action code to complete the verification
            // This will give us access to the user info if needed
            await applyActionCode(auth, oobCode);
            
            // Get the current user (might be signed in after applying action)
            let currentUser = auth.currentUser;
            let userId = null;
            let newEmail = null;
            
            if (currentUser) {
              // User is signed in - reload to get latest email
              await currentUser.reload();
              newEmail = currentUser.email;
              userId = currentUser.uid;
            } else {
              // User not signed in - query Firestore for users with pending email changes
              // Then check if any of those emails exist in Firebase Auth (meaning they're verified)
              try {
                const pendingUsersQuery = query(
                  collection(db, "users"),
                  where("emailChangeStatus", "==", "pending")
                );
                const pendingUsersSnap = await getDocs(pendingUsersQuery);
                
                if (!pendingUsersSnap.empty) {
                  const { fetchSignInMethodsForEmail } = await import('firebase/auth');
                  
                  for (const userDoc of pendingUsersSnap.docs) {
                    const userData = userDoc.data();
                    const pendingEmail = userData.pendingEmailChange;
                    
                    if (pendingEmail && mode === 'verifyAndChangeEmail') {
                      // Check if this pending email exists in Firebase Auth (verified)
                      try {
                        const methods = await fetchSignInMethodsForEmail(auth, pendingEmail);
                        if (methods && methods.length > 0) {
                          // Email exists in Firebase Auth - it's been verified! Sync Firestore
                          userId = userDoc.id;
                          newEmail = pendingEmail;
                          
                          await updateDoc(userDoc.ref, {
                            email: pendingEmail,
                            authEmail: pendingEmail,
                            emailVerified: "Yes",
                            emailChangeStatus: "completed",
                            pendingEmailChange: null,
                            emailChangeTimestamp: null,
                            emailChangeCancellationToken: null
                          });
                          
                          // Found and synced - break out of loop
                          break;
                        }
                      } catch (error) {
                        // Email not found in Firebase Auth yet, continue to next user
                        console.error('Error checking email for user:', error);
                      }
                    }
                  }
                }
              } catch (error) {
                console.error('Error querying pending email changes:', error);
              }
            }
            
            // If we have a user ID and email, sync Firestore
            if (userId && newEmail) {
              const userRef = doc(db, "users", userId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                const pendingEmailChange = userData.pendingEmailChange;
                const emailChangeStatus = userData.emailChangeStatus;
                
                // If this is an email change verification and new email matches pending, sync
                if (mode === 'verifyAndChangeEmail' && 
                    pendingEmailChange && 
                    emailChangeStatus === "pending" &&
                    newEmail === pendingEmailChange) {
                  await updateDoc(userRef, {
                    email: newEmail,
                    authEmail: newEmail,
                    emailVerified: "Yes",
                    emailChangeStatus: "completed",
                    pendingEmailChange: null,
                    emailChangeTimestamp: null,
                    emailChangeCancellationToken: null
                  });
                }
              }
            }
            
            // Clean up URL parameters after processing
            if (window.history && window.history.replaceState) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } catch (error) {
            console.error('Error applying action code:', error);
            // Action code might be invalid or already used - that's okay
          }
        }
      } catch (error) {
        // Silently ignore Firestore internal errors (state machine conflicts during signup/auth changes)
        if (error?.message?.includes('INTERNAL ASSERTION FAILED') || 
            error?.message?.includes('Unexpected state')) {
          return;
        }
        console.error('Error checking pending email changes:', error);
      }
    };
    
    // Run check on mount and when URL changes
    checkAndSyncPendingEmailChanges();
    
    // Check ALL users with pending email changes periodically to auto-sync
    // This handles cases where verification happened but user isn't signed in or is on a different tab
    const checkAllPendingEmailChanges = async () => {
      try {
        // Query all users with pending email changes
        const pendingUsersQuery = query(
          collection(db, "users"),
          where("emailChangeStatus", "==", "pending")
        );
        const pendingUsersSnap = await getDocs(pendingUsersQuery);
        
        if (!pendingUsersSnap.empty) {
          const { fetchSignInMethodsForEmail } = await import('firebase/auth');
          
          for (const userDoc of pendingUsersSnap.docs) {
            const userData = userDoc.data();
            const pendingEmail = userData.pendingEmailChange;
            const userId = userDoc.id;
            const firestoreEmail = userData.email || userData.authEmail;
            
            if (!pendingEmail) continue;
            
            // FIRST: Check if the current signed-in user matches this document AND their email is the pending email
            if (auth.currentUser && auth.currentUser.uid === userId) {
              try {
                await auth.currentUser.reload();
                const currentAuthEmail = auth.currentUser.email;
                const isVerified = auth.currentUser.emailVerified;
                
                // If user is signed in with the new email (pendingEmailChange), sync immediately
                if (currentAuthEmail === pendingEmail && isVerified) {
                  await updateDoc(userDoc.ref, {
                    email: pendingEmail,
                    authEmail: pendingEmail,
                    emailVerified: "Yes",
                    emailChangeStatus: "completed",
                    pendingEmailChange: null,
                    emailChangeTimestamp: null,
                    emailChangeCancellationToken: null
                  });
                  
                  continue; // Move to next user
                }
              } catch (error) {
                console.error('Error checking current user:', error);
              }
            }
            
            // SECOND: If not signed in or email doesn't match, check if pending email exists in Firebase Auth
            // Skip this check to avoid quota limits - rely on onAuthStateChanged instead
            try {
              const methods = await fetchSignInMethodsForEmail(auth, pendingEmail);
              
              if (methods && methods.length > 0) {
                // Pending email exists in Firebase Auth - it's been verified!
                await updateDoc(userDoc.ref, {
                  email: pendingEmail,
                  authEmail: pendingEmail,
                  emailVerified: "Yes",
                  emailChangeStatus: "completed",
                  pendingEmailChange: null,
                  emailChangeTimestamp: null,
                  emailChangeCancellationToken: null
                });
              }
            } catch (error) {
              // Email not found or quota exceeded - skip silently
            }
          }
        }
      } catch (error) {
        // Silently ignore Firestore internal errors (state machine conflicts during signup/auth changes)
        // These are temporary and will resolve on the next check
        if (error?.message?.includes('INTERNAL ASSERTION FAILED') || 
            error?.message?.includes('Unexpected state')) {
          // Skip logging - this is a temporary state during auth transitions
          return;
        }
        console.error('Error checking pending email changes:', error);
      }
    };
    
    // Run check immediately on mount
    checkAllPendingEmailChanges();
    
    // Also check periodically but less frequently to avoid quota limits (every 30 seconds)
    const intervalId = setInterval(checkAllPendingEmailChanges, 30000);
    
    // Listen for email verification events from other tabs
    const handleEmailVerification = async () => {
      // Reload user to get latest auth state
      if (auth.currentUser) {
        try {
          await auth.currentUser.reload();
          // Trigger auth state change by accessing current user
          const refreshedUser = auth.currentUser;
          if (refreshedUser && refreshedUser.emailVerified) {
            // Broadcast that we've reloaded
            if (broadcastChannel) {
              broadcastChannel.postMessage({ 
                type: 'email_verified_reload_complete',
                uid: refreshedUser.uid 
              });
            }
          }
        } catch (error) {
          console.error('Error reloading user after email verification:', error);
        }
      }
    };
    
    if (broadcastChannel) {
      broadcastChannel.addEventListener('message', (event) => {
        if (event.data.type === 'email_verified') {
          handleEmailVerification();
        }
      });
    }
    
    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId);
      if (broadcastChannel) {
        broadcastChannel.close();
      }
    };
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // IMPORTANT: Reload user to get latest email state after verification
        // This ensures we catch email changes immediately after verification
        let refreshedUser = firebaseUser;
        try {
          await firebaseUser.reload();
          refreshedUser = auth.currentUser || firebaseUser;
        } catch (error) {
          console.error('Error reloading user on auth state change:', error);
        }
        
        setUser(refreshedUser);
        setIsAuthenticated(true);
        
        try {
          // Get user data from Firestore
          const userRef = doc(db, "users", refreshedUser.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const data = userSnap.data();
            const role = data.role || data.userType;
            
            setUserRole(role);
            setUserData(data);
            
            // Sync authEmail and email from Firebase Auth to Firestore if they differ
            const currentAuthEmail = refreshedUser.email;
            const firestoreAuthEmail = data.authEmail;
            const isMobileDummyEmail = currentAuthEmail?.includes("@mobileuser.dentalapp.com");
            
            // Check for pending email change
            const pendingEmailChange = data.pendingEmailChange;
            const emailChangeStatus = data.emailChangeStatus;
            
            // MAIN SYNC LOGIC: Check if email change has been verified
            // Case 1: User is signed in with pendingEmailChange - sync immediately!
            const isSignedInWithPendingEmail = pendingEmailChange && 
                                              emailChangeStatus === "pending" &&
                                              currentAuthEmail === pendingEmailChange &&
                                              refreshedUser.emailVerified;
            
            // Case 2: Firestore already has correct email, Firebase Auth matches it, but status is still pending
            const isAlreadySyncedButPending = emailChangeStatus === "pending" &&
                                            currentAuthEmail === firestoreAuthEmail &&
                                            refreshedUser.emailVerified &&
                                            !isMobileDummyEmail;
            
          // Case 3: Email differs from Firestore and is verified (standard sync)
          const needsEmailSync = currentAuthEmail !== firestoreAuthEmail && 
                                !isMobileDummyEmail && 
                                refreshedUser.emailVerified;
          
          if (isSignedInWithPendingEmail || isAlreadySyncedButPending || needsEmailSync) {
            // Email has been changed and verified - automatically update Firestore
            await updateDoc(userRef, {
                email: currentAuthEmail,
                authEmail: currentAuthEmail,
                emailVerified: "Yes",
                emailChangeStatus: "completed",
                pendingEmailChange: null,
                emailChangeCancellationToken: null,
              emailChangeTimestamp: null
            });
            
            // Update local state
              setUserData(prev => ({
                ...prev,
                email: currentAuthEmail,
                authEmail: currentAuthEmail,
                emailVerified: "Yes",
                emailChangeStatus: "completed",
                pendingEmailChange: null,
                emailChangeTimestamp: null
              }));
              
              // Broadcast email verification to other tabs
              if (broadcastChannel) {
                broadcastChannel.postMessage({ 
                  type: 'email_verified',
                  uid: firebaseUser.uid,
                  email: currentAuthEmail 
                });
              }
            }
            
            // Note: Polling removed to prevent Firestore listener conflicts
            // Email sync will happen on next sign-in or when onAuthStateChanged fires
            
            // Check for expired pending email changes (older than a couple of days)
            if (data.emailChangeStatus === "pending" && data.emailChangeTimestamp) {
              const now = Date.now();
              const twoDays = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds
              
              if (now - data.emailChangeTimestamp > twoDays) {
                // Clean up expired pending email change
                await updateDoc(userRef, {
                  emailChangeStatus: "expired",
                  pendingEmailChange: null,
                  emailChangeTimestamp: null,
                  emailChangeCancellationToken: null
                });
                
                // Update local state to reflect the expiration
                setUserData(prev => ({
                  ...prev,
                  emailChangeStatus: "expired",
                  pendingEmailChange: null,
                  emailChangeTimestamp: null,
                emailChangeCancellationToken: null
              }));
              }
            }
          } else {
            console.error('User document not found in Firestore');
            setUserRole(null);
            setUserData(null);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserRole(null);
          setUserData(null);
        }
      } else {
        setUser(null);
        setUserRole(null);
        setUserData(null);
        setIsAuthenticated(false);
      }
      
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
      if (broadcastChannel) {
        broadcastChannel.close();
      }
    };
  }, [auth, db]);

  // Sign out function
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserRole(null);
      setUserData(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Check if user has access to a specific route
  const canAccess = (path) => {
    return hasRouteAccess(userRole, path);
  };

  // Check if user has any of the specified roles
  const hasRole = (roles) => {
    if (!userRole) return false;
    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }
    return userRole === roles;
  };

  // Check if user is admin (DentistOwner)
  const isAdmin = () => {
    return userRole === 'DentistOwner';
  };

  // Check if user is staff (any clinic personnel)
  const isStaff = () => {
    return ['DentistOwner', 'AssociateDentist', 'ClinicStaff'].includes(userRole);
  };

  // Check if user is dentist (owner or associate)
  const isDentist = () => {
    return ['DentistOwner', 'AssociateDentist'].includes(userRole);
  };

  // Check if user is patient
  const isPatient = () => {
    return userRole === 'Patient';
  };

  // Get user's display name
  const getDisplayName = () => {
    if (userData) {
      return `${userData.firstName} ${userData.lastName}`.trim() || userData.email || 'User';
    }
    return user?.email || 'User';
  };

  // Get user's avatar/initials
  const getAvatar = () => {
    if (userData && userData.firstName && userData.lastName) {
      return `${userData.firstName.charAt(0)}${userData.lastName.charAt(0)}`.toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  // Function to update user email (syncs authEmail)
  const updateUserEmail = async (newEmail) => {
    try {
      if (user) {
        // Update Firebase Auth email
        await updateEmail(user, newEmail);
        await sendEmailVerification(user);
        
        // Update Firestore
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, {
          email: newEmail,
          authEmail: newEmail,
          emailVerified: "No",
          emailChangeStatus: "pending",
          emailChangeTimestamp: Date.now()
        });
        
        // Update local state
        setUserData(prev => ({
          ...prev,
          email: newEmail,
          authEmail: newEmail,
          emailVerified: "No"
        }));
      }
    } catch (error) {
      console.error('Error updating email:', error);
      throw error;
    }
  };

  const value = {
    // State
    user,
    userRole,
    userData,
    isLoading,
    isAuthenticated,
    
    // Functions
    logout,
    canAccess,
    hasRole,
    isAdmin,
    isStaff,
    isDentist,
    isPatient,
    getDisplayName,
    getAvatar,
    updateUserEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher-order component for role-based access control
export const withRoleAccess = (WrappedComponent, allowedRoles = []) => {
  return function RoleProtectedComponent(props) {
    const { userRole, isLoading } = useAuth();
    
    if (isLoading) {
      return <SplashScreen isOpen={true} />;
    }
    
    if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: '#fdf8f2'
        }}>
          <div>Access Denied</div>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};
